import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  Optional,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ScannerService } from '../../../services/scanner.service';
import { BarcodeFormat } from '@zxing/library';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { StorageService } from 'src/app/services/storage.service';
import { Camera, CameraDirection } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Subject, takeUntil } from 'rxjs';

// Only import sensors for Android
let sensorsModule: any = null;
let sensorType: any = null;
let sensorDelay: any = null;
if (Capacitor.getPlatform() === 'android') {
  try {
    const {
      Sensors: sensors,
      SensorType: sensorsType,
      SensorDelay: sensorsDelay,
    } = require('@danyalwe/capacitor-sensors');
    sensorsModule = sensors;
    sensorType = sensorsType;
    sensorDelay = sensorsDelay;
  } catch (error) {
    console.error('Failed to load capacitor-sensors module:', error);
  }
}

@Component({
  selector: 'app-scanner-dialog',
  templateUrl: './scanner-dialog.component.html',
  styleUrls: ['./scanner-dialog.component.scss'],
})
export class ScannerDialogComponent implements OnInit, OnDestroy {
  @ViewChild('scanner', { static: false })
  scanner: ZXingScannerComponent;

  allowedFormats = [
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.CODE_128,
    BarcodeFormat.EAN_8,
    BarcodeFormat.EAN_13,
    BarcodeFormat.QR_CODE,
  ];
  public devices: MediaDeviceInfo[] = [];
  public scannerEnabled = true;
  public tryHarderOption = false;
  threshold = 10;
  lightIntensity: number | null = null;
  torchlightEnabled = false;
  autofocus = true;
  currentDevice: MediaDeviceInfo = null;
  videoConstraints: MediaTrackConstraints = {
    width: { min: 800, ideal: 1920 },
    height: { min: 450, ideal: 1080 },
    aspectRatio: { ideal: 1.7777777778 },
  };
  sensor: any = null;
  sensorInitialized = false;

  @Input() instanceId?: string;
  @Input() embedded = false;
  @Output() closed = new EventEmitter<void>();

  private readonly destroyed$ = new Subject<void>();

  constructor(
    @Optional() private dialogRef: MatDialogRef<ScannerDialogComponent> | null,
    private scannerService: ScannerService,
    private localStorage: StorageService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: { instanceId: string }
  ) {
    if (this.dialogRef) {
      this.dialogRef.disableClose = true;
    }
  }

  async ngOnInit() {
    this.localStorage.get('tryHarderOption').then((val) => {
      if (val) {
        this.tryHarderOption = val === 'true';
      }
    });

    if (Capacitor.getPlatform() === 'android' && sensorsModule) {
      try {
        await this.startLightSensor();
      } catch (error) {
        console.error('Error initializing light sensor:', error);
        // Continue without the light sensor
      }
    }
  }

  async ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();

    if (this.scanner) {
      this.scanner.tryHarder = false;
      this.scanner.scanStop();
    }
    this.scannerEnabled = false;
    if (
      Capacitor.getPlatform() === 'android' &&
      sensorsModule &&
      this.sensorInitialized
    ) {
      try {
        await sensorsModule.removeAllListeners();
        if (this.sensor) {
          await sensorsModule.stop(this.sensor);
          this.sensorInitialized = false;
        }
      } catch (error) {
        console.error('Error cleaning up sensors:', error);
      }
    }
    this.torchlightEnabled = false;
  }

  async startLightSensor() {
    if (Capacitor.getPlatform() !== 'android' || !sensorsModule) {
      return;
    }

    try {
      this.sensor = await sensorsModule.init({
        type: sensorType.AMBIENT_LIGHT,
        delay: sensorDelay.NORMAL,
      });

      await sensorsModule.addListener('AMBIENT_LIGHT', (data) => {
        if (data && data.values && data.values.length > 0) {
          this.lightIntensity = data.values[0];
          this.handleLightChange();
        }
      });
      this.sensorInitialized = true;
      await sensorsModule.start(this.sensor);
    } catch (error) {
      console.error('Error starting light sensor:', error);
    }
  }

  async handleLightChange() {
    if (
      this.lightIntensity === null ||
      Capacitor.getPlatform() !== 'android' ||
      !this.scanner
    ) {
      return;
    }

    try {
      if (this.lightIntensity < this.threshold && !this.scanner.torch) {
        this.scanner.torch = true;
        this.torchlightEnabled = true;
      }
    } catch (error) {
      console.error('Error handling light change:', error);
    }
  }

  onScanSuccess(result: string): void {
    if (this.scanner) {
      this.scanner.tryHarder = false;
    }
    const targetInstanceId = this.data?.instanceId || this.instanceId;
    if (targetInstanceId) {
      this.scannerService.sendScanResult(targetInstanceId, result);
    }
    if (!this.embedded && this.dialogRef) {
      this.dialogRef.close(result);
    } else {
      this.closed.emit();
    }
  }

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    const cameraDevices = devices.filter((d) => d.kind === 'videoinput');

    const rearCameras = cameraDevices.filter(
      (device) =>
        device.label.toLowerCase().includes('back') ||
        device.label.toLowerCase().includes('rear')
    );

    this.devices = rearCameras.length > 0 ? rearCameras : cameraDevices;
    setTimeout(() => {
      this.localStorage.get('defaultCamera').then((device) => {
        device = JSON.parse(device);
        if (device) {
          this.currentDevice = devices.find((d) => d.deviceId === device);
          if (!this.currentDevice) {
            this.localStorage.store(
              'defaultCamera',
              devices[devices.length - 1].deviceId
            );
            this.currentDevice = devices[devices.length - 1];
          }
        } else {
          this.localStorage.store(
            'defaultCamera',
            devices[devices.length - 1].deviceId
          );
          this.currentDevice = devices[devices.length - 1];
        }
      });
    }, 1000);
  }

  close(): void {
    if (!this.embedded && this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.closed.emit();
    }
  }

  switchAutoFocus(): void {
    this.autofocus = !this.autofocus;
  }

  onCameraSelect(): void {
    this.localStorage.store('defaultCamera', this.currentDevice.deviceId);
  }

  onFlashlightClick() {
    if (this.torchlightEnabled) {
      this.torchlightEnabled = false;
    } else {
      this.torchlightEnabled = true;
    }
    if (this.scanner) {
      this.scanner.torch = this.torchlightEnabled;
    }
  }
}
